import { parseTimestampMs } from '../../components/formatters.ts';

/**
 * Calendar-day formatting for the admin dashboard's `monthlyWindow`.
 *
 * `./format.ts` owns the presentation helpers the whole `/usage` group
 * shares and is off-limits to this change, so the one helper the seat
 * table needs lives here. Deliberately NOT in `format.ts`'s
 * `formatIsoDate`: that one resolves the browser's zone, and this
 * window is stated in a zone the server names.
 */

/**
 * `YYYY-MM-DD` for an instant read in `timeZone` — NOT in the
 * browser's zone, and NOT by slicing the string.
 *
 * The platform API builds `monthlyWindow` with `shanghaiMonthWindow()`
 * and serialises both ends as UTC `Z` instants, so the start of a
 * Shanghai month arrives as e.g. `2026-07-31T16:00:00.000Z`. Both
 * shortcuts — the browser's local zone, or `iso.slice(0, 10)` (the UTC
 * day) — would label that "2026-07-31" while the caption beside it
 * claims Asia/Shanghai. The boundary has to be resolved in the zone the
 * contract names, which is what `Intl` is for.
 *
 * Anything unparseable, or a zone `Intl` rejects, renders the `--`
 * placeholder: a wrong-but-plausible date is worse than an admitted gap.
 */
export function formatZonedDay(iso: string, timeZone: string): string {
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) {
    return '--';
  }
  return zonedDay(new Date(ms), timeZone) ?? '--';
}

function zonedDay(date: Date, timeZone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    if (year === undefined || month === undefined || day === undefined) {
      return null;
    }
    return `${year}-${month}-${day}`;
  } catch {
    // `Intl` throws RangeError on an unknown zone. The contract pins the
    // value to `Asia/Shanghai`, so this is a guard, not an expected path.
    return null;
  }
}
